import { Button } from '@ordo/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ordo/ui/dialog'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { fetchMyKitchens } from '@/api/kitchen-settings'
import { fetchSchoolOnboardingTasks } from '@/api/school-onboarding'
import { useFirstLoginPasswordPrompt } from '@/components/first-login-password-prompt'
import { missingChefOnboardingDocuments } from '@/lib/chefs/onboarding-documents'
import { useAuthStore } from '@/stores/auth'
import { useProfileStore } from '@/stores/profile'

/** A session reminder, using the existing onboarding wizard and upload rules. */
export function ChefOnboardingDocumentsPrompt() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const dismissed = useAuthStore((state) => state.dismissedLoginPrompts.includes('chefDocuments'))
  const dismiss = useAuthStore((state) => state.dismissLoginPrompt)
  const profile = useProfileStore((state) => state.profile)
  const passwordPrompt = useFirstLoginPasswordPrompt()
  const enabled = Boolean(user && profile?.profileCodes.includes('CHEF_ADMIN') && !dismissed)
  const documents = useQuery({
    // Scoped to the login identity; completing an upload also invalidates this
    // through the wizard's existing ['school-onboarding'] invalidation.
    queryKey: ['school-onboarding', 'chef-document-reminder', user?.id],
    queryFn: async ({ signal }) => {
      const [kitchens, tasks] = await Promise.all([fetchMyKitchens(signal), fetchSchoolOnboardingTasks(signal)])
      return missingChefOnboardingDocuments(kitchens.me?.kitchenOrganizations ?? [], tasks.tasks)
    },
    enabled,
  })

  const missing = documents.data ?? []
  if (
    !enabled ||
    passwordPrompt.isPending ||
    passwordPrompt.open ||
    !documents.isSuccess ||
    documents.isFetching ||
    !missing.length
  ) {
    return null
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          dismiss('chefDocuments')
        }
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pl-0 pr-8 text-left sm:pr-8">
          <DialogTitle className="leading-snug">A quick document reminder</DialogTitle>
          <DialogDescription className="leading-relaxed">
            Hey Chef, we’re still missing a few documents for your kitchen. You can upload them whenever you’re ready.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-3">
            {missing.map((task) => (
              <section key={task.taskId} className="rounded-lg bg-muted/40 p-4">
                <h3 className="mb-3 text-sm font-medium">{task.kitchenName}</h3>
                <ul
                  aria-label={`Missing documents for ${task.kitchenName}`}
                  className="list-disc space-y-2 pl-4 text-sm leading-relaxed marker:text-muted-foreground">
                  {task.labels.map((label, index) => (
                    <li key={`${index}-${label}`} className="pl-1">
                      {label.replace(/^upload\s+/i, '')}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Find this request anytime in Tasks or My Kitchen. We’ll remind you on your next sign-in if documents are
            still missing.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => dismiss('chefDocuments')}>
            Remind me later
          </Button>
          <Button
            type="button"
            onClick={() => {
              dismiss('chefDocuments')
              navigate(`/onboarding/${encodeURIComponent(missing[0]!.taskId)}`)
            }}>
            Upload documents
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
